import { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import {
  Search,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  FileDown,
  Inbox,
  BookOpen,
} from 'lucide-react'
import { cn } from '../../lib/utils'
import { Button } from './Button'

export interface DataTableColumn<T> {
  header: string
  cell: (row: T) => React.ReactNode
  sortable?: boolean
  sortFn?: (a: T, b: T) => number
}

export interface DataTableEmptyState {
  icon?: React.ReactNode
  title: string
  description?: string
  action?: React.ReactNode
  tutorialSlug?: string
  tutorialTitle?: string
}

interface DataTableProps<T> {
  columns: DataTableColumn<T>[]
  data: T[]
  keyExtractor: (row: T) => string
  isLoading?: boolean
  searchable?: boolean
  searchPlaceholder?: string
  searchFn?: (row: T, query: string) => boolean
  pagination?: boolean
  pageSize?: number
  pageSizeOptions?: number[]
  selectable?: boolean
  selectedIds?: string[]
  onSelectionChange?: (ids: string[]) => void
  emptyState?: DataTableEmptyState
  actions?: (row: T) => React.ReactNode
  onRowClick?: (row: T) => void
  exportable?: boolean
  exportFileName?: string
  searchValue?: string
  onSearchChange?: (value: string) => void
  filterFn?: (row: T) => boolean
  className?: string
}

type SortDir = 'asc' | 'desc' | null

export function DataTable<T>({
  columns,
  data,
  keyExtractor,
  isLoading,
  searchable = true,
  searchPlaceholder = 'Buscar...',
  searchFn,
  pagination = true,
  pageSize: defaultPageSize = 10,
  pageSizeOptions = [10, 25, 50],
  selectable = false,
  selectedIds = [],
  onSelectionChange,
  emptyState,
  actions,
  onRowClick,
  exportable = false,
  exportFileName = 'datos.csv',
  searchValue,
  onSearchChange,
  filterFn,
  className,
}: DataTableProps<T>) {
  const [internalSearch, setInternalSearch] = useState('')
  const search = searchValue ?? internalSearch
  const handleSearchChange = (value: string) => {
    if (onSearchChange) onSearchChange(value)
    else setInternalSearch(value)
    setPage(1)
  }
  const [sortCol, setSortCol] = useState<number | null>(null)
  const [sortDir, setSortDir] = useState<SortDir>(null)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(defaultPageSize)

  const filtered = useMemo(() => {
    let result = [...data]
    const q = search.trim().toLowerCase()
    if (q) {
      if (searchFn) {
        result = result.filter((row) => searchFn(row, q))
      } else {
        result = result.filter((row) =>
          JSON.stringify(row).toLowerCase().includes(q)
        )
      }
    }
    if (filterFn) {
      result = result.filter(filterFn)
    }
    if (sortCol != null && sortDir && columns[sortCol]?.sortFn) {
      const fn = columns[sortCol].sortFn!
      result.sort((a, b) => (sortDir === 'asc' ? fn(a, b) : fn(b, a)))
    }
    return result
  }, [data, search, searchFn, filterFn, sortCol, sortDir, columns])

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize))
  const safePage = Math.min(page, totalPages)
  const paginated = pagination
    ? filtered.slice((safePage - 1) * pageSize, safePage * pageSize)
    : filtered

  const allSelectedOnPage =
    paginated.length > 0 && paginated.every((row) => selectedIds.includes(keyExtractor(row)))
  const someSelectedOnPage =
    paginated.some((row) => selectedIds.includes(keyExtractor(row))) && !allSelectedOnPage

  function toggleSort(colIndex: number) {
    if (sortCol === colIndex) {
      setSortDir((d) => (d === 'asc' ? 'desc' : d === 'desc' ? null : 'asc'))
      if (sortDir === 'desc') setSortCol(null)
    } else {
      setSortCol(colIndex)
      setSortDir('asc')
    }
    setPage(1)
  }

  function toggleSelectAll() {
    if (!onSelectionChange) return
    const pageIds = paginated.map(keyExtractor)
    if (allSelectedOnPage) {
      onSelectionChange(selectedIds.filter((id) => !pageIds.includes(id)))
    } else {
      const newSet = new Set([...selectedIds, ...pageIds])
      onSelectionChange(Array.from(newSet))
    }
  }

  function toggleSelectRow(id: string) {
    if (!onSelectionChange) return
    if (selectedIds.includes(id)) {
      onSelectionChange(selectedIds.filter((x) => x !== id))
    } else {
      onSelectionChange([...selectedIds, id])
    }
  }

  function exportCsv() {
    const headers = columns.map((c) => c.header).join(';')
    const rows = filtered.map((row) =>
      columns
        .map((col) => {
          const node = col.cell(row)
          const text = extractText(node)
          return `"${text.replace(/"/g, '""')}"`
        })
        .join(';')
    )
    const csv = [headers, ...rows].join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = exportFileName
    a.click()
    URL.revokeObjectURL(url)
  }

  const startItem = filtered.length === 0 ? 0 : (safePage - 1) * pageSize + 1
  const endItem = Math.min(safePage * pageSize, filtered.length)

  return (
    <div className={cn('space-y-4', className)}>
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        {searchable && (
          <div className="flex items-center bg-surface-1 border border-border rounded-lg px-4 py-2.5 max-w-md w-full">
            <Search className="w-4 h-4 text-text-tertiary mr-3 flex-shrink-0" />
            <input
              type="text"
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder={searchPlaceholder}
              className="bg-transparent text-sm text-text-primary placeholder-text-tertiary outline-none w-full"
            />
          </div>
        )}
        <div className="flex items-center gap-2">
          {selectable && selectedIds.length > 0 && (
            <span className="text-xs font-medium text-text-secondary">
              {selectedIds.length} seleccionado{selectedIds.length > 1 ? 's' : ''}
            </span>
          )}
          {exportable && (
            <Button variant="secondary" size="sm" onClick={exportCsv}>
              <FileDown className="w-4 h-4" />
              Exportar
            </Button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="bg-surface-1 border border-border rounded-xl overflow-hidden">
        <div className="overflow-x-auto [-webkit-overflow-scrolling:touch]">
          <table className="w-full min-w-[640px]">
            <thead>
              <tr className="bg-surface-2 border-b border-border">
                {selectable && (
                  <th className="px-2 py-2 md:px-4 md:py-3 w-10">
                    <input
                      type="checkbox"
                      checked={allSelectedOnPage}
                      ref={(el) => {
                        if (el) el.indeterminate = someSelectedOnPage
                      }}
                      onChange={toggleSelectAll}
                      className="w-4 h-4 rounded border-border text-primary-600 focus:ring-primary-300"
                    />
                  </th>
                )}
                {columns.map((col, i) => (
                  <th
                    key={i}
                    className={cn(
                      'text-left text-[11px] font-semibold text-text-primary uppercase tracking-wider px-2 py-2 md:px-4 md:py-3 whitespace-nowrap',
                      col.sortable && 'cursor-pointer select-none'
                    )}
                    onClick={() => col.sortable && toggleSort(i)}
                  >
                    <div className="flex items-center gap-1">
                      {col.header}
                      {col.sortable && (
                        <span className="text-text-tertiary">
                          {sortCol === i ? (
                            sortDir === 'asc' ? (
                              <ArrowUp className="w-3 h-3" />
                            ) : (
                              <ArrowDown className="w-3 h-3" />
                            )
                          ) : (
                            <ArrowUpDown className="w-3 h-3" />
                          )}
                        </span>
                      )}
                    </div>
                  </th>
                ))}
                {actions && <th className="px-2 py-2 md:px-4 md:py-3 w-10" />}
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                Array.from({ length: pageSize }).map((_, i) => (
                  <tr key={i} className="border-b border-border-subtle">
                    <td
                      colSpan={
                        columns.length + (selectable ? 1 : 0) + (actions ? 1 : 0)
                      }
                      className="px-2 py-3 md:px-4 md:py-4"
                    >
                      <div className="h-4 bg-surface-2 rounded animate-pulse w-3/4" />
                    </td>
                  </tr>
                ))
              ) : (
                <>
                  {paginated.map((row) => {
                    const id = keyExtractor(row)
                    return (
                      <tr
                        key={id}
                        className={cn(
                          'border-b border-border-subtle transition-colors',
                          onRowClick
                            ? 'hover:bg-surface-2 cursor-pointer'
                            : 'hover:bg-surface-2'
                        )}
                        onClick={() => onRowClick?.(row)}
                      >
                        {selectable && (
                          <td
                            className="px-2 py-2 md:px-4 md:py-3"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <input
                              type="checkbox"
                              checked={selectedIds.includes(id)}
                              onChange={() => toggleSelectRow(id)}
                              className="w-4 h-4 rounded border-border text-primary-600 focus:ring-primary-300"
                            />
                          </td>
                        )}
                        {columns.map((col, ci) => (
                          <td key={ci} className="px-4 py-3">
                            {col.cell(row)}
                          </td>
                        ))}
                        {actions && (
                          <td
                            className="px-2 py-2 md:px-4 md:py-3"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="flex items-center gap-1">
                              {actions(row)}
                            </div>
                          </td>
                        )}
                      </tr>
                    )
                  })}
                  {paginated.length === 0 && (
                    <tr>
                      <td
                        colSpan={
                          columns.length + (selectable ? 1 : 0) + (actions ? 1 : 0)
                        }
                        className="px-2 py-8 md:px-4 md:py-12"
                      >
                        <div className="flex flex-col items-center justify-center text-center">
                          {emptyState?.icon ?? (
                            <Inbox className="w-10 h-10 text-text-tertiary mb-3" />
                          )}
                          <p className="text-sm font-medium text-text-primary">
                            {emptyState?.title ?? 'No hay datos'}
                          </p>
                          {emptyState?.description && (
                            <p className="text-sm text-text-tertiary mt-1 max-w-xs">
                              {emptyState.description}
                            </p>
                          )}
                          {emptyState?.action && (
                            <div className="mt-4">{emptyState.action}</div>
                          )}
                          {emptyState?.tutorialSlug && (
                            <div className="mt-3 flex items-center gap-1.5 text-xs text-primary-600 hover:text-primary-700">
                              <BookOpen className="w-3.5 h-3.5" />
                              <Link to={`/tutorials/${emptyState.tutorialSlug}`}>
                                Ver tutorial{emptyState.tutorialTitle ? `: ${emptyState.tutorialTitle}` : ''}
                              </Link>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {pagination && filtered.length > 0 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="text-xs text-text-tertiary">
              Mostrando <span className="font-medium text-text-secondary">{startItem}</span>–
              <span className="font-medium text-text-secondary">{endItem}</span> de{' '}
              <span className="font-medium text-text-secondary">{filtered.length}</span>
            </span>
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value))
                setPage(1)
              }}
              className="text-xs bg-surface-1 border border-border rounded-md px-2 py-1 text-text-primary outline-none focus:ring-1 focus:ring-primary-300"
            >
              {pageSizeOptions.map((s) => (
                <option key={s} value={s}>
                  {s} / página
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-1.5">
            <button
              onClick={() => setPage(1)}
              disabled={safePage === 1}
              className="p-1.5 rounded-lg border border-border bg-surface-1 text-text-secondary hover:bg-surface-2 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronsLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={safePage === 1}
              className="p-1.5 rounded-lg border border-border bg-surface-1 text-text-secondary hover:bg-surface-2 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>

            <span className="text-xs font-medium text-text-secondary px-2 min-w-[4rem] text-center">
              {safePage} / {totalPages}
            </span>

            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={safePage === totalPages}
              className="p-1.5 rounded-lg border border-border bg-surface-1 text-text-secondary hover:bg-surface-2 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
            <button
              onClick={() => setPage(totalPages)}
              disabled={safePage === totalPages}
              className="p-1.5 rounded-lg border border-border bg-surface-1 text-text-secondary hover:bg-surface-2 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronsRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

function extractText(node: React.ReactNode): string {
  if (node == null) return ''
  if (typeof node === 'string' || typeof node === 'number') return String(node)
  if (Array.isArray(node)) return node.map(extractText).join(' ')
  if (typeof node === 'object' && 'props' in node) {
    return extractText((node as any).props.children)
  }
  return ''
}
